import { useEffect } from "react";
import { useForm } from "react-hook-form";
import FormModal from "@/components/FormModal/FormModal";
import formRenderer from "@/lib/formRenderer";
import type { FormConfig } from "@/lib/formRenderer";
import PlusIcon from "@/components/SVGComponents/PlusIcon";
import { notBlankValidator, requiredMessage } from "@/lib/utils";
import type { SupportItem } from "@/types/support";
import styles from "./SupportFormModal.module.scss";

export interface SupportFormValues {
  question: string;
  answer: string;
}

// Inclusive limits: exactly 300 / 2000 characters is valid. Also set as the
// textarea's native `maxLength`, so typing stops at the limit.
const QUESTION_MAX_LENGTH = 300;
const ANSWER_MAX_LENGTH = 2000;

const SUPPORT_FORM_CONFIG: FormConfig = {
  question: {
    controlType: "textarea",
    label: "Question",
    labelVariant: "caption",
    placeholder: "e.g. Is Subscription Refundable?",
    validations: {
      required: requiredMessage("Question"),
      validate: notBlankValidator("Question"),
      maxLength: { value: QUESTION_MAX_LENGTH, message: `Question must be at most ${QUESTION_MAX_LENGTH} characters` },
    },
    props: { maxLength: QUESTION_MAX_LENGTH },
  },
  answer: {
    controlType: "textarea",
    label: "Answer",
    labelVariant: "caption",
    placeholder: "Enter the answer members will see",
    validations: {
      required: requiredMessage("Answer"),
      validate: notBlankValidator("Answer"),
      maxLength: { value: ANSWER_MAX_LENGTH, message: `Answer must be at most ${ANSWER_MAX_LENGTH} characters` },
    },
    props: { maxLength: ANSWER_MAX_LENGTH },
  },
};

export interface SupportFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  // Present → "Edit support item" (prefilled from this row). Absent/null →
  // "Add support item".
  supportItem?: SupportItem | null;
  // May be async (Support.tsx's real create/update call) — `handleFormSubmit`
  // below awaits it so `formState.isSubmitting` (and so every field's
  // disabled-while-submitting state) reflects the real request, not just
  // the synchronous moment this function is called.
  onSubmit: (values: SupportFormValues) => void | Promise<void>;
}

const SupportFormModal = ({ isOpen, onClose, supportItem = null, onSubmit }: SupportFormModalProps) => {
  const isEditing = supportItem !== null;

  const { register, handleSubmit, reset, formState } = useForm<SupportFormValues>({
    defaultValues: { question: "", answer: "" },
  });

  // This same modal instance is reused for both "Add support item" and
  // every row's "Edit" click — the form has to be explicitly reset to that
  // item's values each time it opens, since useForm's own `defaultValues`
  // are only read once at mount (same convention as OpenChapterModal.tsx).
  useEffect(() => {
    if (!isOpen) return;
    reset({ question: supportItem?.question ?? "", answer: supportItem?.answer ?? "" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, supportItem]);

  const handleClose = () => {
    reset();
    onClose();
  };

  // Leading/trailing spaces (and blank lines) are trimmed before saving so
  // the mobile app never shows them — covers both add and edit.
  const handleFormSubmit = handleSubmit(async (values) => {
    await onSubmit({ question: values.question.trim(), answer: values.answer.trim() });
  });

  return (
    <FormModal
      isOpen={isOpen}
      onClose={handleClose}
      title={isEditing ? "Edit support item" : "Add support item"}
      subtitle={isEditing ? "Update this question and its answer." : "Add a new question to the support list."}
      onSubmit={handleFormSubmit}
      submitLabel={isEditing ? "Save changes" : "Add support item"}
      submitIcon={<PlusIcon width={14} height={14} />}
      submitDisabled={formState.isSubmitting}
      className={styles.modal}
    >
      {formRenderer<SupportFormValues>(SUPPORT_FORM_CONFIG, formState, register)}
    </FormModal>
  );
};

export default SupportFormModal;
